// src/services/gameGeneratorService.js
//
// Firestore side of the Game generator.
//
//   generatedGames/{slug}  the DRAFT: every game type made from the lyrics,
//                          plus what you picked. Students never read it.
//   quizzes/{slug}         the LIVE game. This is the only place the game
//                          player (UniversalQuizGame) and the portal's Game
//                          button look, so publishing writes here.
//   quizBackups/{id}       a copy of whatever was in quizzes/{slug} before a
//                          publish replaced it. Nothing is ever lost.
//
// One thing still wins over quizzes/{slug}: questions typed straight into a
// topic on a page in the Layout Builder. That is how the game player has
// always worked; the panel says so where it matters.

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase/firebaseConfig";
import { questionsForPublish } from "./gameGenerator";
import { exactKey } from "../utils/topicKey";

const who = () => {
  const u = getAuth().currentUser;
  return u?.email || u?.uid || "unknown";
};

// saveDraft and publishGame write to EXACTLY the key they are given. The panel
// works that key out with makeToolKey (src/utils/topicKey.js): the old
// stripped slug for every ordinary topic, the topic's own exact slug when
// stripping would collide with another topic (earth'swater vs earthswater).
// Re-stripping it here is what made two topics share one game.
const docKey = (s) => {
  const id = exactKey(s);
  if (!id) throw new Error("A game needs a topic slug.");
  return id;
};

/** slug -> { gameType, count, generated } for every live quiz document. */
export const getLiveGames = async () => {
  const snap = await getDocs(collection(db, "quizzes"));
  const out = {};
  snap.forEach((d) => {
    const data = d.data() || {};
    const n = Array.isArray(data.questions) ? data.questions.length : 0;
    if (!n) return;
    const entry = {
      gameType: data.gameType || "multipleChoice",
      bonusRoundType: data.bonusRoundType || "",
      count: n,
      generated: Boolean(data.generated),
    };
    // Exact ids only. The panel resolves a topic the way the game player
    // does (exact slug, then stripped), so it can tell a topic's OWN game
    // from one it only shares with a look-alike topic. Indexing stripped ids
    // here as well made earthswater appear to own earth'swater's game.
    out[d.id.toLowerCase()] = { ...entry, id: d.id };
  });
  return out;
};

export const getDrafts = async () => {
  const snap = await getDocs(collection(db, "generatedGames"));
  const out = {};
  snap.forEach((d) => {
    out[d.id] = { id: d.id, ...d.data() };
  });
  return out;
};

/** Strips UI-only fields before saving. */
const cleanContent = (content) => JSON.parse(JSON.stringify(content || {}));

export const saveDraft = async (slug, draft) => {
  const id = docKey(slug);
  const payload = {
    slug: id,
    title: draft.title || id,
    subject: draft.subject || "",
    seed: draft.seed,
    count: draft.count,
    lyricsHash: draft.lyricsHash,
    content: cleanContent(draft.content),
    spare: cleanContent(draft.spare || []),
    available: draft.available || {},
    gameType: draft.gameType,
    bonusRoundType: draft.bonusRoundType,
    status: draft.status || "draft",
    updatedAt: serverTimestamp(),
    updatedBy: who(),
  };
  await setDoc(doc(db, "generatedGames", id), payload, { merge: true });
  return payload;
};

/**
 * Makes a draft the live game.
 *
 * 1. Copies the current quizzes/{slug} (if any) into quizBackups.
 * 2. Replaces quizzes/{slug} with the chosen game type's questions.
 * 3. Marks the draft published.
 */
export const publishGame = async ({ slug, title, topicImage, draft, gameType, bonusRoundType }) => {
  const id = docKey(slug);
  const ref = doc(db, "quizzes", id);
  const before = await getDoc(ref);
  let backupId = null;
  if (before.exists()) {
    const b = await addDoc(collection(db, "quizBackups"), {
      slug: id,
      quiz: before.data(),
      reason: "replaced by Game generator publish",
      backedUpAt: serverTimestamp(),
      backedUpBy: who(),
    });
    backupId = b.id;
  }

  const questions = questionsForPublish(draft.content, gameType);
  const quiz = {
    slug: id,
    title: title || id,
    gameType,
    bonusRoundType,
    questions,
    generated: true,
    generatedFrom: "lyrics",
    generatedSeed: draft.seed || "",
    publishedAt: serverTimestamp(),
    publishedBy: who(),
  };
  // Puzzle Shuffle needs a picture; the topic's own image is the natural one.
  if (topicImage) quiz.placeholderImage = topicImage;

  await setDoc(ref, quiz);
  await saveDraft(id, { ...draft, gameType, bonusRoundType, status: "published" });
  return { backupId, count: questions.length };
};
